// src/components/WhatsappMarketingPortal.jsx

import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import styles from "./MarketingPortal.module.css"; // We can reuse the same CSS
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";

const functions = getFunctions(app);

export default function WhatsappMarketingPortal() {
  const [allStudents, setAllStudents] = useState([]);
  const [fileName, setFileName] = useState("");
  const [startRow, setStartRow] = useState("1");
  const [endRow, setEndRow] = useState("0");

  const [contentSid, setContentSid] = useState("");
  const [contentVariables, setContentVariables] = useState([""]);

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setStatus({ type: "info", message: "Reading file..." });

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        setAllStudents(json);
        setStartRow("1");
        setEndRow(String(json.length));
        setStatus({ type: "success", message: `Loaded ${json.length} rows` });
      } catch (err) {
        console.error(err);
        setStatus({ type: "error", message: "Failed to parse file: " + err.message });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleVariableChange = (i, v) => {
    const copy = [...contentVariables];
    copy[i] = v;
    setContentVariables(copy);
  };
  const addVariable = () => setContentVariables([...contentVariables, ""]);
  const removeVariable = (i) => setContentVariables(contentVariables.filter((_, idx) => idx !== i));

  const handleSendCampaign = async () => {
    setReport(null);
    const start = parseInt(startRow, 10);
    const end = parseInt(endRow, 10);
    if (isNaN(start) || isNaN(end) || start < 1 || end < start || end > allStudents.length) {
      setStatus({ type: "error", message: "Invalid start/end rows." });
      return;
    }

    if (!contentSid.trim()) {
      setStatus({ type: "error", message: "WhatsApp template SID is required." });
      return;
    }

    // Create the payload for the WhatsApp function
    const messagePayload = {
      contentSid: contentSid.trim(),
      variables: contentVariables.map((v) => v.trim()),
    };

    const studentsToSend = allStudents.slice(start - 1, end);
    setIsLoading(true);
    setStatus({ type: "info", message: `Triggering WhatsApp campaign for ${studentsToSend.length} recipients...` });

    try {
      // ✨ Call the new 'triggerBulkWhatsapp' function
      const fn = httpsCallable(functions, "triggerBulkWhatsapp");
      const payload = {
        students: studentsToSend,
        messagePayload,
        // 'channel' is no longer needed
      };
      const res = await fn(payload);
      setReport(res.data);
      setStatus({ type: "success", message: res.data.message || "Campaign completed." });
    } catch (err) {
      console.error("Callable error:", err);
      setStatus({ type: "error", message: "Error sending campaign: " + (err.message || JSON.stringify(err)) });
    } finally {
      setIsLoading(false);
    }
  };

  const isSendDisabled = useMemo(() => isLoading || allStudents.length === 0, [isLoading, allStudents]);

  return (
    <div className={styles.portalContainer}>
      <div className={styles.portalHeader}>
        <h1>WhatsApp Marketing Portal</h1>
        <p>Upload Excel and send bulk WhatsApp templates via Twilio</p>
      </div>

      <div className={styles.step}>
        <h2>Step 1: Upload Student List (Excel)</h2>
        <input id="file-upload" type="file" accept=".xlsx, .xls, .csv" className={styles.fileInput} onChange={handleFileUpload} />
        <label htmlFor="file-upload" className={styles.fileLabel}>
          {fileName ? `File: ${fileName}` : "Choose Excel File"}
        </label>
        <p style={{ marginTop: 8 }}>Excel must have columns: <strong>name, phone</strong> (with country code)</p>
      </div>

      {allStudents.length > 0 && (
        <>
          <div className={styles.step}>
            <h2>Step 2: Select Batch</h2>
            <p className={styles.totalCount}>Total Rows: <strong>{allStudents.length}</strong></p>
            <div className={styles.rowSelector}>
              <div className={styles.inputGroup}>
                <label>Start Row</label>
                <input type="number" value={startRow} min="1" max={allStudents.length} onChange={(e) => setStartRow(e.target.value)} />
              </div>
              <div className={styles.inputGroup}>
                <label>End Row</label>
                <input type="number" value={endRow} min="1" max={allStudents.length} onChange={(e) => setEndRow(e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <button onClick={() => { setStartRow("1"); setEndRow(String(allStudents.length)); }} data-tooltip="Select All Students">Select All</button>
            </div>
          </div>

          <div className={styles.step}>
            <h2>Step 3: Compose WhatsApp Message</h2>
            
            {/* Removed the channel selector and conditional logic */}
            <div className={styles.templateForm}>
              <div className={styles.inputGroup}>
                <label>WhatsApp Template SID</label>
                <input value={contentSid} onChange={(e) => setContentSid(e.target.value)} placeholder="e.g. HXxxxxxxxx" />
              </div>
              <label className={styles.variableLabel}>Template variables (in order)</label>
              {contentVariables.map((v, idx) => (
                <div key={idx} className={styles.variableInput}>
                  <span>{`{{${idx + 1}}}`}</span>
                  <input value={v} onChange={(e) => handleVariableChange(idx, e.target.value)} placeholder={`Value ${idx + 1}`} />
                  <button onClick={() => removeVariable(idx)} className={styles.removeButton} data-tooltip="Remove Variable">&times;</button>
                </div>
              ))}
              <button onClick={addVariable} className={styles.addButton} data-tooltip="Add New Variable">+ Add Variable</button>
            </div>
          </div>

          <div className={styles.step}>
            <h2>Step 4: Launch Campaign</h2>
            <button onClick={handleSendCampaign} disabled={isSendDisabled} className={styles.sendButton} data-tooltip="Send WhatsApp Campaign">
              {isLoading ? "Sending..." : `Send to ${parseInt(endRow, 10) - parseInt(startRow, 10) + 1 || 0} recipients`}
            </button>
          </div>

          <div className={styles.step}>
            <h2>Preview (first 10 rows)</h2>
            <div style={{ maxHeight: 240, overflow: "auto", border: "1px solid #e5e7eb", padding: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 6 }}>#</th>
                    <th style={{ textAlign: "left", padding: 6 }}>name</th>
                    <th style={{ textAlign: "left", padding: 6 }}>email</th>
                    <th style={{ textAlign: "left", padding: 6 }}>phone</th>
                  </tr>
                </thead>
                <tbody>
                  {allStudents.slice(0, 10).map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: 6 }}>{i + 1}</td>
                      <td style={{ padding: 6 }}>{r.name}</td>
                      <td style={{ padding: 6 }}>{r.email}</td>
                      <td style={{ padding: 6 }}>{r.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {status && (
        <div className={`${styles.statusMessage} ${styles[status.type || "info"]}`} style={{ marginTop: 12 }}>
          {status.message}
        </div>
      )}

      {report && (
        <div style={{ marginTop: 12 }}>
          <h3>Campaign Report</h3>
          <pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 12, borderRadius: 8 }}>
            {JSON.stringify(report, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}